import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { biorhythmAdvice, calcBiorhythms } from '@yadviga/shared'
import { ChevronRight } from 'lucide-react'

/**
 * Small profile block — three circular gauges showing today's biorhythm
 * values plus a single advice line. Tap → full /profile/biorhythms page.
 */
export function BiorhythmsBlock({ birthDate }: { birthDate: string }) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language === 'en' ? 'en' : 'ru') as 'ru' | 'en'
  const values = calcBiorhythms(birthDate)
  const advice = biorhythmAdvice(values, lang)

  return (
    <Link
      to="/profile/biorhythms"
      className="flex flex-col gap-3 rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg text-sage-800">{t('bio.today_title')}</h2>
        <ChevronRight className="h-4 w-4 text-sage-400" strokeWidth={1.8} aria-hidden />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Gauge value={values.physical}     label={t('bio.physical')} />
        <Gauge value={values.emotional}    label={t('bio.emotional')} />
        <Gauge value={values.intellectual} label={t('bio.intellectual')} />
      </div>
      <p className="text-sm leading-relaxed text-sage-700">{advice.text}</p>
    </Link>
  )
}

function Gauge({ value, label }: { value: number; label: string }) {
  // value in -1..+1 → render as a percent for both the arc length and
  // the text. Positive = sage-600 (good), negative = copper (caution).
  const pct = Math.round(value * 100)
  const positive = value >= 0
  const color = positive ? 'text-sage-600' : 'text-copper'
  const radius = 22
  const circ = 2 * Math.PI * radius
  const offset = circ - (Math.abs(value) * circ) / 2 // half-circle scale
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-14 w-14">
        <svg viewBox="0 0 56 56" className="h-14 w-14">
          <circle cx="28" cy="28" r={radius} stroke="currentColor" strokeOpacity="0.15" strokeWidth="4" fill="none" />
          <circle
            cx="28" cy="28" r={radius}
            stroke="currentColor" strokeWidth="4" fill="none"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 28 28)"
            className={color}
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-xs font-medium ${color}`}>
          {pct > 0 ? `+${pct}` : pct}
        </span>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-sage-500">{label}</span>
    </div>
  )
}
